<template>
  <!-- 排期甘特：按项目/人员/迭代批次维度的任务排期时间轴 + 工时冲突预警（只读） -->
  <div class="schedule-gantt">
    <list-page>
      <search-condition @search="loadAll" :show-search="true">
        <template #default>
          <el-select v-model="dim" placeholder="分组维度">
            <template #prefix>维度</template>
            <el-option label="按项目" value="project"/>
            <el-option label="按人员" value="person"/>
            <el-option label="按迭代" value="iteration"/>
            <el-option label="按任务" value="task"/>
          </el-select>
          <el-select v-model="projectId" placeholder="全部项目" clearable filterable>
            <template #prefix>所属项目</template>
            <el-option v-for="p in projectOptions" :key="p.id" :label="(p.projectCode || '') + ' ' + (p.projectName || '')" :value="p.id"/>
          </el-select>
          <el-date-picker class="double-width"
              v-model="range"
              type="daterange"
              range-separator="至"
              start-placeholder="开始日期"
              end-placeholder="结束日期"
              value-format="YYYY-MM-DD"
              unlink-panels/>
        </template>
        <template #option>
          <page-button type="read" el-type="primary" @click="loadAll">刷新视图</page-button>
        </template>
      </search-condition>

      <!-- 甘特时间轴 -->
      <div v-loading="loading" class="gantt-panel">
        <div v-if="!hasTasks" class="gantt-empty">当前维度下暂无任务</div>
        <template v-else>
          <div class="gantt-scroll">
            <div class="gantt-inner" :style="{minWidth: labelWidth + trackWidth + 'px'}">
              <!-- 表头：左列固定名 + 月份刻度 -->
              <div class="gantt-head">
                <div class="g-label head-label">{{ headLabel }}</div>
                <div class="g-months">
                  <div v-for="m in months" :key="m.label" class="g-month"
                       :style="{width: m.days * dayW + 'px'}">{{ m.label }}</div>
                </div>
              </div>
              <!-- 主体行：每个分组占一行，行内并排展示该组的全部任务条 -->
              <div class="gantt-body">
                <div v-for="g in groups" :key="g.key" class="gantt-row">
                  <div class="g-label">
                    <!-- 按任务：任务名称 + 编号/项目/负责人/工天 -->
                    <template v-if="dim === 'task'">
                      <div class="group-name">{{ g.label }}</div>
                      <div class="task-meta">
                        <span class="code-text">{{ g.tasks[0].taskCode || '—' }}</span>
                        <span>{{ g.tasks[0].projectName || '—' }}</span>
                        <span>{{ g.tasks[0].ownerName || '未分配' }}</span>
                        <span v-if="g.tasks[0].estimate != null">{{ g.tasks[0].estimate }} 工天</span>
                        <span v-if="isDelay(g.tasks[0])" class="delay-text">{{ delayText(g.tasks[0]) }}</span>
                      </div>
                    </template>
                    <!-- 按项目：名称 + 编号/提出人/类型/优先级 -->
                    <template v-else-if="dim === 'project'">
                      <div class="group-name">{{ g.label }}</div>
                      <div class="task-meta">
                        <span class="code-text">{{ g.tasks[0].projectCode || '—' }}</span>
                        <span>提出人：{{ g.tasks[0].projectSourceName || '—' }}</span>
                        <span>{{ g.tasks[0].projectType || '—' }}</span>
                        <span>{{ g.tasks[0].projectPriority || '—' }}</span>
                      </div>
                    </template>
                    <!-- 按迭代：名称 + 编号/项目经理/期望完成 -->
                    <template v-else-if="dim === 'iteration'">
                      <div class="group-name">{{ g.label }}</div>
                      <div class="task-meta">
                        <span class="code-text">{{ g.tasks[0].sprintCode || '—' }}</span>
                        <span>项目经理：{{ g.tasks[0].sprintManagerName || '—' }}</span>
                        <span>期望：{{ dateOnly(g.tasks[0].sprintExpectDate) }}</span>
                      </div>
                    </template>
                    <!-- 按人员：名称 + 项目数/任务数/延期数/计划时间范围 -->
                    <template v-else>
                      <div class="group-name">{{ g.label }}</div>
                      <div class="task-meta">
                        <span>{{ g.projectCount }} 个项目</span>
                        <span>{{ g.tasks.length }} 个任务</span>
                        <span v-if="g.delayCount" class="delay-text">已延期 {{ g.delayCount }} 个</span>
                        <span v-else>无延期</span>
                        <span v-if="g.dateRange">{{ g.dateRange }}</span>
                      </div>
                    </template>
                  </div>
                  <div class="g-track" :style="{width: trackWidth + 'px'}">
                    <div v-if="todayInRange()" class="today-line" :style="{left: todayLeft() + 'px'}"/>
                    <template v-for="task in g.tasks" :key="task.id">
                      <el-tooltip v-if="hasDates(task)" placement="top" :show-after="120" :content="barTitle(task)">
                        <div class="g-bar" :class="barClass(task)"
                             :style="{left: barLeft(task) + 'px', width: barWidth(task) + 'px'}">
                          <!-- 进度比例条：宽度=进度%，叠加在状态底色之上直观展示完成比例 -->
                          <div class="g-bar-progress" :style="{width: progressPct(task) + '%'}"/>
                          <span class="g-bar-text">{{ task.taskName }} {{ task.progress || 0 }}%</span>
                        </div>
                      </el-tooltip>
                    </template>
                    <span v-if="!hasAnyDate(g)" class="no-date">未排期</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <!-- 图例 -->
          <div class="gantt-legend">
            <span><i class="dot todo"/></span>未开始
            <span><i class="dot doing"/></span>进行中
            <span><i class="dot done"/></span>已完成
            <span><i class="dot delay"/></span>已延期
            <span><i class="today-line-sample"/></span>今天
          </div>
        </template>
      </div>

      <!-- 排期冲突与工时超载预警（按节点平均工时>8h/天 且未完成） -->
      <div class="conflict-panel">
        <div class="conflict-head">排期冲突与工时超载预警<span class="muted">（节点平均工时 &gt; 8 h/天 且未完成）</span></div>
        <el-table :data="overloadRows" border size="small" :empty-text="'当前无工时超载节点'">
          <el-table-column label="任务" min-width="180">
            <template v-slot="{row}">
              <span class="link-task" @click="openTaskDetail(row)">{{ row.taskName || '未命名任务' }}</span>
              <div class="muted code-text">{{ row.taskCode || '—' }}</div>
            </template>
          </el-table-column>
          <el-table-column label="节点" prop="nodeName" min-width="140"/>
          <el-table-column label="负责人" prop="ownerName" width="90">
            <template v-slot="{row}">{{ row.ownerName || '—' }}</template>
          </el-table-column>
          <el-table-column label="计划" width="200">
            <template v-slot="{row}">{{ dateOnly(row.planStart) }} ~ {{ dateOnly(row.planEnd) }}</template>
          </el-table-column>
          <el-table-column label="总工时(h)" prop="hours" width="90" align="center"/>
          <el-table-column label="平均工时" width="110" align="right">
            <template v-slot="{row}"><span class="delay-text">{{ row.hoursAvg }} h/日</span></template>
          </el-table-column>
          <el-table-column label="说明" width="150">
            <template v-slot="{row}">超出可用工时 {{ CAPACITY }} h/日</template>
          </el-table-column>
        </el-table>
      </div>
    </list-page>
  </div>
</template>

<script>
import {useRoute, useRouter} from "vue-router";
import Util from "@/util/util";
import scheduleApi from "@/api/dz-project/project/scheduleApi";
import {TaskStatusCode} from "../manage/projectStatus";

// 每天占位像素（决定时间轴总宽），按需调整保证可读
const DAY_W = 26;
// 左列宽度
const LABEL_W = 300;
// 默认日产能（外部员工无产能字段，固定 8h/日）
const CAPACITY = 8;

// 日期运算统一按 'YYYY-MM-DD' 字符处理（避免时区）
function parseDay(d) {
  if (!d) {
    return NaN;
  }
  const s = String(d).slice(0, 10);
  return Math.round(Date.parse(s) / 86400000);
}
function dayDiff(a, b) {
  return parseDay(b) - parseDay(a);
}
function addDays(d, n) {
  return new Date((parseDay(d) + n) * 86400000).toISOString().slice(0, 10);
}

export default {
  setup() {
    return {router: useRouter(), route: useRoute()};
  },
  name: "ScheduleGantt",
  data() {
    return {
      dim: "task",
      projectId: "",
      range: [],           // 排期时间范围 [start, end]（与区间有交集即命中）
      dayW: DAY_W,
      labelWidth: LABEL_W,
      loading: false,
      taskList: [],        // 接口原始任务（非已取消）
      overloadRows: [],    // 节点工时超载（平均工时>8 且未完成）
      CAPACITY: CAPACITY,
      projectOptions: [],
    };
  },
  computed: {
    /** 左列表头（随维度变化） */
    headLabel() {
      return {task: "任务", project: "项目", person: "人员", iteration: "迭代"}[this.dim] || "任务 / 分组";
    },
    /** 展示任务：按所选项目过滤（空=全部） */
    visibleTasks() {
      const list = this.taskList || [];
      const projectFilter = this.projectId;
      return list.filter(t => !projectFilter || t.projectId === projectFilter);
    },
    /** 有明确排期日期范围的任务（决定时间轴 min~max） */
    datedTasks() {
      return this.visibleTasks.filter(t => this.hasDates(t));
    },
    hasTasks() {
      return !!this.datedTasks.length;
    },
    minDate() {
      if (!this.datedTasks.length) {
        return "";
      }
      let min = this.datedTasks[0].planStart;
      this.datedTasks.forEach(t => {
        if (t.planStart < min) {
          min = t.planStart;
        }
      });
      return addDays(min, -2);
    },
    maxDate() {
      if (!this.datedTasks.length) {
        return "";
      }
      let max = this.datedTasks[0].planEnd;
      this.datedTasks.forEach(t => {
        if (t.planEnd > max) {
          max = t.planEnd;
        }
      });
      return addDays(max, 2);
    },
    totalDays() {
      if (!this.minDate || !this.maxDate) {
        return 0;
      }
      return dayDiff(this.minDate, this.maxDate) + 1;
    },
    /** 时间轴总宽（px），每行 track 与月份表头共用同一坐标 */
    trackWidth() {
      return this.totalDays * this.dayW;
    },
    /** 月份表头分段 [{label:'yyyy-MM', days}] */
    months() {
      if (!this.hasTasks) {
        return [];
      }
      const segs = [];
      for (let i = 0; i < this.totalDays; i++) {
        const d = addDays(this.minDate, i);
        const m = d.slice(0, 7);
        const last = segs[segs.length - 1];
        if (!last || last.label !== m) {
          segs.push({label: m, days: 1});
        } else {
          last.days++;
        }
      }
      return segs;
    },
    todayStr() {
      return new Date().toISOString().slice(0, 10);
    },
    /**
     * 按维度分组：
     *  - 按项目：一个项目一行
     *  - 按人员：一个人员一行
     *  - 按迭代：一个迭代一行（含未挂批次的「未安排迭代」）
     *  - 按任务：一个任务一行
     * 每个分组只占一行，行内并排展示该组的全部任务条。
     */
    groups() {
      const t = this.visibleTasks;
      // 按任务：每个任务一组
      if (this.dim === "task") {
        return t.map(x => ({key: x.id, label: x.taskName || "未命名任务", sub: "", dateRange: "", tasks: [x]}));
      }
      const map = new Map();
      const keyOf = (x) => {
        if (this.dim === "person") {
          return x.ownerId || "__none__";
        }
        if (this.dim === "iteration") {
          return x.sprintId || "__none__";
        }
        return x.projectId || "__none__";
      };
      t.forEach(x => {
        const key = keyOf(x);
        if (!map.has(key)) {
          map.set(key, {
            key,
            label: this.groupLabel(x),
            sub: "",
            dateRange: "",
            tasks: [],
          });
        }
        map.get(key).tasks.push(x);
      });
      return Array.from(map.values()).map(g => {
        g.sub = g.tasks.length + " 个任务";
        // 人员维度统计：涉及项目数 / 延期任务数
        g.projectCount = new Set(g.tasks.map(x => x.projectId || "__none__")).size;
        g.delayCount = g.tasks.filter(x => this.isDelay(x)).length;
        const dated = g.tasks.filter(x => this.hasDates(x));
        if (dated.length) {
          let start = dated[0].planStart;
          let end = dated[0].planEnd;
          dated.forEach(x => {
            if (x.planStart < start) {
              start = x.planStart;
            }
            if (x.planEnd > end) {
              end = x.planEnd;
            }
          });
          g.dateRange = this.dateOnly(start) + " ~ " + this.dateOnly(end);
        }
        return g;
      });
    },
  },
  beforeRouteLeave(to, from, next) {
    Util.cachePageData(this, to, from);
    next();
  },
  created() {
    this.initOptions();
    this.loadAll();
  },
  methods: {
    async initOptions() {
      const list = await scheduleApi.queryProjectOptions();
      this.projectOptions = list || [];
    },
    async loadAll() {
      this.loading = true;
      try {
        const params = {};
        if (this.projectId) {
          params.projectId = this.projectId;
        }
        // 排期时间范围（与区间有交集即命中）
        if (this.range && this.range.length === 2 && this.range[0] && this.range[1]) {
          params.rangeStart = this.range[0];
          params.rangeEnd = this.range[1];
        }
        const [list, overload] = await Promise.all([
          scheduleApi.queryList(params),
          scheduleApi.queryOverloadNodes(params),
        ]);
        this.taskList = list || [];
        this.overloadRows = overload || [];
      } finally {
        this.loading = false;
      }
    },
    /** 分组标签（按当前维度取不同名称） */
    groupLabel(x) {
      if (this.dim === "person") {
        return x.ownerName || (x.ownerId ? "未命名" : "未分配");
      }
      if (this.dim === "iteration") {
        return x.sprintName || "未安排迭代";
      }
      return x.projectName || "未关联项目";
    },
    /** 打开任务详情 */
    openTaskDetail(row) {
      if (row && row.taskId) {
        this.router.push({path: "/project/dev/task/detail", query: {id: row.taskId}});
      }
    },
    dateOnly(v) {
      return v ? String(v).slice(0, 10) : "—";
    },
    hasDates(t) {
      return !!(t && t.planStart && t.planEnd);
    },
    /** 分组内是否至少有一个任务已排期 */
    hasAnyDate(g) {
      return !!(g && g.tasks && g.tasks.some(t => this.hasDates(t)));
    },
    isDelay(task) {
      return !!(task.delayState && task.delayState !== "正常");
    },
    delayText(task) {
      if (task.delayState === "已延期完成") {
        return "延期完成";
      }
      if (task.delayState === "延期中") {
        return "延期 " + (task.delayDays || 0) + " 天";
      }
      return task.delayState || "";
    },
    barClass(task) {
      if (this.isDelay(task)) {
        return "delay";
      }
      if (task.taskStatus === TaskStatusCode.DONE) {
        return "done";
      }
      if (task.taskStatus === TaskStatusCode.DOING) {
        return "doing";
      }
      return "todo";
    },
    barTitle(task) {
      return `${task.taskName}（${this.dateOnly(task.planStart)} ~ ${this.dateOnly(task.planEnd)}，进度 ${task.progress || 0}%）`;
    },
    barLeft(task) {
      return dayDiff(this.minDate, task.planStart) * this.dayW;
    },
    barWidth(task) {
      return (dayDiff(task.planStart, task.planEnd) + 1) * this.dayW;
    },
    // 进度比例：用于进度条填充宽度(%)，缺省或非法值按0，超出0~100按边界收敛
    progressPct(task) {
      const p = Number(task && task.progress);
      if (!isFinite(p) || p <= 0) {
        return 0;
      }
      return p >= 100 ? 100 : p;
    },
    // 今天竖线是否落在时间轴范围内
    todayInRange() {
      return !!(this.minDate && this.maxDate
        && this.todayStr >= this.minDate && this.todayStr <= this.maxDate);
    },
    todayLeft() {
      return dayDiff(this.minDate, this.todayStr) * this.dayW;
    },
  },
};
</script>

<style lang="scss" scoped>
.schedule-gantt {
  height: 100%;
  display: flex;
  flex-direction: column;
  .gantt-panel {
    flex: 1;
    min-height: 0;
    display: flex;
    flex-direction: column;
    background: #fff;
    border-radius: 4px;
    border: 1px solid var(--yq-border-color);
    margin-bottom: 10px;
    .gantt-empty {
      padding: 48px 0;
      text-align: center;
      color: var(--yq-font-color-desc);
    }
    .gantt-scroll {
      flex: 1;
      overflow: auto;
      .gantt-inner {
        // 头部月份刻度与下方逐行 track 共用同一套像素坐标（labelWidth + 天数*dayW）
        width: fit-content;
        .gantt-head, .gantt-row {
          display: flex;
          align-items: center;
          border-bottom: 1px solid var(--yq-border-color);
        }
        .gantt-head {
          position: sticky;
          top: 0;
          z-index: 3;
          background: #fafbfd;
          font-weight: 600;
          .g-label {
            flex: none;
          }
          .g-months {
            flex: none;
            display: flex;
            .g-month {
              text-align: center;
              font-size: 12px;
              color: var(--yq-font-color-desc);
              border-right: 1px solid var(--el-border-color-lighter);
              box-sizing: border-box;
            }
          }
        }
        .g-label {
          flex: none;
          width: 300px;
          box-sizing: border-box;
          padding: 6px 10px;
          border-right: 1px solid var(--el-border-color-lighter);
          overflow: hidden;
          .group-name {
            //font-weight: 600;
            font-size: var(--yq-font-size-small);
            color: var(--el-color-primary);
            margin-bottom: 5px;
          }
          small {
            margin-left: 6px;
            color: var(--yq-font-color-desc);
          }
        }
        .g-track {
          flex: none;
          position: relative;
          height: 40px;
          box-sizing: border-box;
          .no-date {
            position: absolute;
            top: 12px;
            left: 8px;
            font-size: 12px;
            color: #bbb;
          }
        }
        .gantt-row {
          position: relative;
          &:hover {
            background: #fbfcfe;
          }
          .task-name {
            font-size: 13px;
            white-space: nowrap;
            overflow: hidden;
            text-overflow: ellipsis;
          }
          .task-meta {
            font-size: var(--yq-font-size-mini);
            color: var(--yq-font-color-desc);
            display: flex;
            flex-wrap: wrap;
            gap: 0 8px;
            .code-text {
              font-family: Consolas, Menlo, monospace;
            }
            .delay-text {
              color: #f56c6c;
              font-weight: 600;
            }
            .sprint-tag {
              color: #409eff;
            }
          }
          .g-bar {
            position: absolute;
            top: 12px;
            height: 18px;
            border-radius: 3px;
            color: #fff;
            font-size: 11px;
            line-height: 18px;
            padding: 0 4px;
            box-sizing: border-box;
            overflow: hidden;
            white-space: nowrap;
            cursor: default;

            // 进度比例条：叠在状态底色之上，宽度即完成百分比
            .g-bar-progress {
              position: absolute;
              left: 0;
              top: 0;
              bottom: 0;
              border-radius: 3px 0 0 3px;
              transition: width .2s;
            }
            // 任务名与百分比文字，置于进度条之上保证可读
            .g-bar-text {
              position: relative;
              display: block;
              overflow: hidden;
              text-overflow: ellipsis;
            }
          }
          // 状态底色（未完成部分）+ 进度条填充色（已完成部分，同色系加亮）
          .g-bar.todo { background: #90a4c4; .g-bar-progress { background: #5c7fae; } }
          .g-bar.doing { background: #4fc3f7; .g-bar-progress { background: #0288d1; } }
          .g-bar.done { background: #66bb6a; .g-bar-progress { background: #2e7d32; } }
          .g-bar.delay { background: #ef9a9a; .g-bar-progress { background: #c62828; } }
          // 周网格线
          .g-track::before {
            content: "";
            position: absolute;
            inset: 0;
            background-image: repeating-linear-gradient(90deg, transparent 0 calc(7 * 26px - 1px), #eef1f6 calc(7 * 26px - 1px) calc(7 * 26px));
          }
          .today-line {
            position: absolute;
            top: 0;
            bottom: 0;
            width: 2px;
            background: #c62828;
            z-index: 2;
          }
        }
      }
    }
    .gantt-legend {
      padding: 6px 12px;
      font-size: 12px;
      color: var(--yq-font-color-desc);
      display: flex;
      align-items: center;
      gap: 6px;
      border-top: 1px solid var(--el-border-color-lighter);
      .dot {
        display: inline-block;
        width: 12px;
        height: 12px;
        border-radius: 3px;
        margin-left: 8px;
        &.todo { background: #90a4c4; }
        &.doing { background: #0288d1; }
        &.done { background: #2e7d32; }
        &.delay { background: #c62828; }
      }
      .today-line-sample {
        display: inline-block;
        width: 2px;
        height: 12px;
        background: #c62828;
        margin-left: 8px;
      }
    }
  }
  .conflict-panel {
    background: #fff;
    border: 1px solid var(--yq-border-color);
    border-radius: 4px;
    padding: 10px;
    .conflict-head {
      font-weight: 600;
      margin-bottom: 8px;
      .muted {
        font-size: 12px;
        color: var(--yq-font-color-desc);
        font-weight: normal;
        margin-left: 8px;
      }
    }
    .delay-text {
      color: #f56c6c;
    }
  }
}
</style>
